import {useContext, useMemo} from 'react';
import {useTranslation} from 'react-i18next';

import {ClientContext} from 'context';
import {SessionActivity, SessionActivityName} from 'interfaces';
import {TRANSLATION_KEYS} from 'locale';
import {PreSessionFlowView} from 'pages/Client';
import {PreSessionFlowState, PreSessionFormFieldInputType, PreSessionStepStatus, PreSessionStepType} from 'types';

const T_KEYS = TRANSLATION_KEYS.USER.CONTENT.CLIENT_SESSION.WHITE_LABEL.PREVIEW_MODAL.SAMPLE_PRE_SESSION_FLOW;

const FORM_STEP_ID = 'preview-form-step';
const SCREENSHOT_STEP_ID = 'preview-screenshot-step';

// "label" is the userInfo path the value is mirrored into, "title" is what the client sees.
const FIRST_NAME_FIELD_PATH = 'firstName';
const EMAIL_FIELD_PATH = 'contacts.email';

const getFlowState: (TFunction) => PreSessionFlowState = (t) => ({
    flowSnapshot: {
        enabled: true,
        steps: [
            {
                id: FORM_STEP_ID,
                name: t(T_KEYS.FORM_STEP_NAME),
                type: PreSessionStepType.Form,
                required: true,
                clientPrompt: t(T_KEYS.FORM_STEP_PROMPT),
                form: {
                    fields: [
                        {
                            id: 'preview-first-name-field',
                            label: FIRST_NAME_FIELD_PATH,
                            title: t(T_KEYS.FIRST_NAME_FIELD),
                            inputType: PreSessionFormFieldInputType.Text,
                            required: true
                        },
                        {
                            id: 'preview-email-field',
                            label: EMAIL_FIELD_PATH,
                            title: t(T_KEYS.EMAIL_FIELD),
                            inputType: PreSessionFormFieldInputType.Email,
                            required: true
                        }
                    ]
                }
            },
            {
                id: SCREENSHOT_STEP_ID,
                name: t(T_KEYS.SCREENSHOT_STEP_NAME),
                type: PreSessionStepType.Screenshot,
                required: true,
                clientPrompt: t(T_KEYS.SCREENSHOT_STEP_PROMPT),
                screenshot: {
                    name: t(T_KEYS.SCREENSHOT_STEP_NAME)
                }
            }
        ]
    },
    currentStepIndex: 0,
    steps: [
        {stepId: FORM_STEP_ID, status: PreSessionStepStatus.InProgress, attempts: 0},
        {stepId: SCREENSHOT_STEP_ID, status: PreSessionStepStatus.Pending, attempts: 0}
    ]
});

export const PreSessionFlowPreview: React.FC = () => {
    const {t} = useTranslation();
    const clientContext = useContext(ClientContext);

    // The view reads the flow snapshot from the default activity data, so the preview session
    // gets a sample activity carrying it.
    const previewContext = useMemo(() => {
        const activity: SessionActivity = {
            name: SessionActivityName.TextChat,
            startDate: new Date().toISOString(),
            data: {preSessionFlow: getFlowState(t)}
        };

        return {
            ...clientContext,
            session: {
                ...clientContext.session,
                activities: [activity]
            }
        };
    }, [clientContext, t]);

    return (
        <ClientContext.Provider value={previewContext}>
            <PreSessionFlowView />
        </ClientContext.Provider>
    );
};
